import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Snackbar, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import {
  HOUR_HEIGHT,
  DEFAULT_SCROLL_HOUR,
  dateToMinutes,
  formatTimeFromMinutes,
  buildDayColumnsTemplate,
  columnBoundaries,
  xToDayWeighted,
} from './calendarLayout';
import {
  startDrag,
  updateDrag,
  finalizeDrag,
  clientYToMinute,
  type DragState,
  type DragResult,
} from './calendarDrag';
import CalendarDayColumn from './CalendarDayColumn';
import { colors } from '../../../core/theme/theme';
import { isSameLocalDay, sumDurationSeconds, formatDurationSeconds } from '../timeEntries';
import { dayKey, nonWorkingDay } from '../workingDays';
import { useTimeLock } from '../useTimeLock';

const GUTTER_WIDTH = 60;

export interface CalendarGridProps {
  days: Date[];
  entries: Array<{ id: string; data: TimeEntry }>;
  projectNames: Map<string, string>;
  designationNames: Map<string, string>;
  projectColors: Map<string, string | undefined>;
  isViewingTeammate: boolean;
  readOnlyTooltip: string;
  selectedEntryId: string | null;
  onSelectEntry: (entryId: string) => void;
  onEntryEdit: (entryId: string, x: number, y: number) => void;
  onHoverPositionChange: (pos: { dayIndex: number; minute: number } | null) => void;
  onEmptySlotClick: (dayIndex: number, minute: number, x: number, y: number) => void;
  onDragMove: (entryId: string, result: DragResult, anchorDate: Date) => void;
  onDragResize: (entryId: string, result: DragResult) => void;
  /**
   * When true (default, desktop), the grid owns its own vertical scroll so
   * day headers stay pinned above the time cells. When false (mobile), the
   * scroll is delegated to an ancestor so the whole page scrolls as one unit.
   */
  nestedScroll?: boolean;
}

export default function CalendarGrid({
  days,
  entries,
  projectNames,
  designationNames,
  projectColors,
  isViewingTeammate,
  readOnlyTooltip,
  selectedEntryId,
  onSelectEntry,
  onEntryEdit,
  onHoverPositionChange,
  onEmptySlotClick,
  onDragMove,
  onDragResize,
  nestedScroll = true,
}: CalendarGridProps) {
  const { t } = useTranslation();
  const { isMonthClosed } = useTimeLock();
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  // Tracks whether the last drag was committed (moved). Used to suppress the
  // synthetic click event that browsers fire after pointerup on the same target.
  const dragCommittedRef = useRef(false);
  const [ghostBlock, setGhostBlock] = useState<{
    day: number;
    top: number;
    height: number;
  } | null>(null);
  const [nowMinute, setNowMinute] = useState(() => dateToMinutes(new Date()));
  // Days the user explicitly opened for input this week. Deliberately not
  // persisted: navigating away re-arms the guard.
  const [unlockedDays, setUnlockedDays] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const [unlockHint, setUnlockHint] = useState<string | null>(null);

  const today = new Date();
  const dayCount = days.length;

  // Why each displayed day is (or isn't) a working day — computed once and
  // shared by the header, the columns and the click guard.
  const dayStates = useMemo(
    () =>
      days.map((day) => {
        const reason = nonWorkingDay(day);
        return {
          reason,
          locked: reason !== null && !unlockedDays.has(dayKey(day)),
          // Clôture mensuelle — définitive côté utilisateur, contrairement
          // à `locked` juste au-dessus qu'un clic rouvre.
          closed: isMonthClosed(day),
        };
      }),
    [days, unlockedDays, isMonthClosed],
  );

  // Re-arm the guard whenever the visible week changes.
  const weekKey = days.length > 0 ? dayKey(days[0]) : 0;
  useEffect(() => {
    setUnlockedDays(new Set());
  }, [weekKey]);

  const unlockDay = useCallback(
    (dayIndex: number) => {
      const day = days[dayIndex];
      const reason = nonWorkingDay(day);
      setUnlockedDays((prev) => new Set(prev).add(dayKey(day)));
      setUnlockHint(
        reason?.kind === 'holiday'
          ? t('kronos.calendar.unlockedHoliday', { name: reason.name })
          : t('kronos.calendar.unlockedWeekend'),
      );
    },
    [days, t],
  );

  const colBounds = useMemo(() => columnBoundaries(dayCount), [dayCount]);
  const dayColsTemplate = useMemo(
    () => buildDayColumnsTemplate(dayCount),
    [dayCount],
  );

  // Scroll to DEFAULT_SCROLL_HOUR (7h) on mount when the grid owns its scroll.
  // Use rAF to ensure the layout is resolved before scrolling —
  // without this, scrollTop silently fails because the container
  // hasn't computed its overflow yet.
  // When nestedScroll is false the page scrolls the grid, and the hero is
  // intentionally the first thing the user sees, so we skip the jump.
  useEffect(() => {
    if (!nestedScroll) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = DEFAULT_SCROLL_HOUR * HOUR_HEIGHT;
      }
    });
  }, [nestedScroll]);

  // Update "now" line every 60s
  useEffect(() => {
    const interval = setInterval(
      () => setNowMinute(dateToMinutes(new Date())),
      60_000,
    );
    return () => clearInterval(interval);
  }, []);

  // Group entries by day
  const entriesByDay = days.map((day) =>
    entries.filter((e) => isSameLocalDay(e.data.startTime.toDate(), day)),
  );

  // ─── Drag helpers ──────────────────────────────────────────────

  const getGridMetrics = useCallback(() => {
    const el = gridRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      rect,
      bodyWidth: rect.width - GUTTER_WIDTH,
      bodyLeft: rect.left + GUTTER_WIDTH,
    };
  }, []);

  const handleEntryPointerDown = useCallback(
    (
      e: React.PointerEvent,
      entryId: string,
      startMin: number,
      endMin: number,
    ) => {
      if (isViewingTeammate) return;
      const metrics = getGridMetrics();
      if (!metrics) return;

      // metrics.rect is the scrolling grid *content*, whose top already
      // reflects scrollTop — do not add it again (see clientYToMinute).
      const minute = clientYToMinute(e.clientY, metrics.rect.top, HOUR_HEIGHT);

      const resizeHandle = (e.target as HTMLElement).dataset.resize;
      const type =
        resizeHandle === 'start'
          ? ('resize-start' as const)
          : resizeHandle === 'end'
            ? ('resize-end' as const)
            : ('move' as const);

      const x = e.clientX - metrics.bodyLeft;
      const day = xToDayWeighted(x, metrics.bodyWidth, colBounds);

      dragRef.current = startDrag(
        type,
        day,
        minute,
        entryId,
        startMin,
        endMin,
      );
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [isViewingTeammate, getGridMetrics, colBounds],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const metrics = getGridMetrics();
      if (!metrics) return;

      const minute = clientYToMinute(e.clientY, metrics.rect.top, HOUR_HEIGHT);
      const x = e.clientX - metrics.bodyLeft;
      const day = xToDayWeighted(x, metrics.bodyWidth, colBounds);

      // Always report the hovered slot so keyboard paste (Ctrl+V) knows
      // where the cursor is — keydown events carry no pointer position.
      onHoverPositionChange({ dayIndex: day, minute });

      if (!dragRef.current) return;

      dragRef.current = updateDrag(dragRef.current, day, minute);

      if (dragRef.current.committed) {
        const result = finalizeDrag(dragRef.current);
        const top = (result.startMinute / 60) * HOUR_HEIGHT;
        const height =
          ((result.endMinute - result.startMinute) / 60) * HOUR_HEIGHT;
        setGhostBlock({ day: result.day, top, height });
      }
    },
    [getGridMetrics, colBounds, onHoverPositionChange],
  );

  const handlePointerUp = useCallback(
    (_e: React.PointerEvent) => {
      const state = dragRef.current;
      dragRef.current = null;
      setGhostBlock(null);
      if (!state) return;

      const result = finalizeDrag(state);

      if (state.committed) {
        // Suppress the click event that the browser fires after pointerup on
        // the same captured element — we don't want to open the edit modal
        // after a successful move or resize.
        dragCommittedRef.current = true;
        setTimeout(() => { dragCommittedRef.current = false; }, 0);
      }

      switch (state.type) {
        case 'move': {
          if (!state.entryId || !state.committed) break;
          const anchorDate = days[result.day] ?? days[0];
          onDragMove(state.entryId, result, anchorDate);
          break;
        }
        case 'resize-start':
        case 'resize-end': {
          if (!state.entryId || !state.committed) break;
          onDragResize(state.entryId, result);
          break;
        }
      }
    },
    [days, onDragMove, onDragResize],
  );

  const checkDragCommitted = useCallback(() => dragCommittedRef.current, []);

  // ─── Hour labels (0–23) ────────────────────────────────────────

  const hourLabels = Array.from({ length: 24 }, (_, i) => i);
  const gridColTemplate = `${GUTTER_WIDTH}px ${dayColsTemplate}`;

  return (
    <Box
      data-testid="calendar-grid"
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      {/* ── Fixed column headers (outside scroll) ───────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColTemplate,
          borderBottom: `1px solid ${colors.lightGray}`,
          flexShrink: 0,
        }}
      >
        {/* Empty gutter cell */}
        <Box />

        {/* Day headers */}
        {days.map((day, i) => {
          const isToday = isSameLocalDay(day, today);
          const { reason } = dayStates[i];
          const dayName = day.toLocaleDateString(undefined, {
            weekday: 'short',
          });
          const dayNum = day.getDate();
          const monthShort = day.toLocaleDateString(undefined, {
            month: 'short',
          });
          const totalSec = sumDurationSeconds(
            entriesByDay[i].map((e) => e.data),
          );

          return (
            <Box
              key={i}
              sx={{
                textAlign: 'center',
                py: 0.75,
                px: 0.5,
                bgcolor: isToday
                  ? alpha(colors.kronosAccent, 0.08)
                  : reason
                    ? alpha(colors.lightGray, 0.35)
                    : 'background.paper',
                borderLeft:
                  i > 0
                    ? `1px solid ${colors.lightGray}`
                    : undefined,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: isToday ? 700 : 500,
                  color: reason ? colors.textMuted : undefined,
                }}
              >
                {dayName} {dayNum} {monthShort}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block' }}
                title={reason?.kind === 'holiday' ? reason.name : undefined}
              >
                {reason?.kind === 'holiday'
                  ? [reason.name, totalSec > 0 ? formatDurationSeconds(totalSec) : null]
                      .filter(Boolean)
                      .join(' · ')
                  : formatDurationSeconds(totalSec)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── Scrollable time grid ────────────────────────────── */}
      <Box
        ref={scrollRef}
        sx={{
          flex: nestedScroll ? 1 : '0 0 auto',
          overflow: nestedScroll ? 'auto' : 'visible',
          minHeight: 0,
        }}
      >
        <Box
          ref={gridRef}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridColTemplate,
            height: 24 * HOUR_HEIGHT,
            position: 'relative',
          }}
        >
          {/* Hour gutter */}
          <Box
            sx={{
              position: 'relative',
              borderRight: `1px solid ${colors.lightGray}`,
            }}
          >
            {hourLabels.map((h) => (
              <Box
                key={h}
                sx={{
                  height: HOUR_HEIGHT,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  pr: 1,
                  pt: 0.25,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {formatTimeFromMinutes(h * 60)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Day columns */}
          {days.map((day, i) => (
            <Box
              key={i}
              sx={{
                borderRight:
                  i < dayCount - 1
                    ? `1px solid ${colors.lightGray}`
                    : undefined,
                position: 'relative',
              }}
            >
              {/* Gridlines */}
              {hourLabels.map((h) => (
                <Box
                  key={h}
                  sx={{
                    height: HOUR_HEIGHT,
                    borderBottom: `1px solid ${colors.lightGray}`,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: HOUR_HEIGHT / 2 - 1,
                      left: 0,
                      right: 0,
                      borderBottom: `1px dashed ${alpha(colors.lightGray, 0.6)}`,
                    },
                  }}
                />
              ))}

              {/* Day column with entries (overlaid) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                <CalendarDayColumn
                  dayIndex={i}
                  entries={entriesByDay[i]}
                  projectNames={projectNames}
                  designationNames={designationNames}
                  projectColors={projectColors}
                  nonWorking={dayStates[i].reason}
                  locked={dayStates[i].locked}
                  onUnlock={() => unlockDay(i)}
                  monthClosed={dayStates[i].closed}
                  closedTooltip={t('kronos.timeLock.tooltip')}
                  isViewingTeammate={isViewingTeammate}
                  readOnlyTooltip={readOnlyTooltip}
                  selectedEntryId={selectedEntryId}
                  onSelectEntry={onSelectEntry}
                  onEntryEdit={onEntryEdit}
                  onEmptySlotClick={onEmptySlotClick}
                  onEntryPointerDown={handleEntryPointerDown}
                  checkDragCommitted={checkDragCommitted}
                />
              </Box>

              {/* "Now" line */}
              {isSameLocalDay(day, today) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: (nowMinute / 60) * HOUR_HEIGHT,
                    left: 0,
                    right: 0,
                    borderTop: `2px solid ${colors.red}`,
                    zIndex: 3,
                    pointerEvents: 'none',
                  }}
                />
              )}

              {/* Ghost block during drag */}
              {ghostBlock && ghostBlock.day === i && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: ghostBlock.top,
                    height: ghostBlock.height,
                    left: 4,
                    right: 4,
                    bgcolor: alpha(colors.kronosAccent, 0.2),
                    border: `1px dashed ${colors.kronosAccent}`,
                    borderRadius: '4px',
                    zIndex: 4,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Tells the user the day just opened and a second click will create. */}
      <Snackbar
        open={unlockHint !== null}
        autoHideDuration={4000}
        onClose={() => setUnlockHint(null)}
        message={unlockHint}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
